import { useToast, type DataTableProps, type MenuItem } from '@ui';
import { useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../../../app/ConfirmDialog';
import { useRowMenu } from '../../../app/rowActions';
import { errorMessage } from '../../../lib/errors';
import { formatDate } from '../../../lib/format';
import { formatEuro } from '../../sales/format';
import { useSession } from '../../identity/session';
import { useDeleteEntry, useReceipt, type Entry } from '../api';
import { EntryFormDialog } from './EntryFormDialog';

/**
 * The `…` menu of an entry row (#197): edit, attach a receipt and delete,
 * like on the entry's page. Automatic entries change with their sale (FIN-007).
 */
export function useEntryRowActions(): {
  rowActions: NonNullable<DataTableProps<Entry>['rowActions']>;
  dialogs: ReactNode;
} {
  const { t } = useTranslation();
  const toast = useToast();
  const { can } = useSession();
  const rowMenu = useRowMenu();
  const remove = useDeleteEntry();
  const receipt = useReceipt();
  const file = useRef<HTMLInputElement>(null);
  const target = useRef<string | null>(null);
  const [open, setOpen] = useState<{ kind: 'edit' | 'delete'; entry: Entry } | null>(null);
  const write = can('finance.write');

  const items = (e: Entry): (MenuItem | false)[] => {
    const manual = e.source.kind === 'manual';
    return [
      write && manual && rowMenu.edit(() => setOpen({ kind: 'edit', entry: e })),
      write && {
        key: 'receipt',
        label: e.receipt ? t('finance.receipt.replace') : t('finance.receipt.attach'),
        onSelect: () => {
          target.current = e.id;
          file.current?.click();
        },
      },
      write && manual && rowMenu.remove(() => setOpen({ kind: 'delete', entry: e })),
    ];
  };

  const dialogs = (
    <>
      <input
        ref={file}
        type="file"
        accept="image/*,application/pdf"
        hidden
        aria-label={t('finance.receipt.choose')}
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          const id = target.current;
          if (!f || !id) return;
          receipt.mutate(
            { id, file: f },
            {
              onSuccess: () => toast.show(t('finance.receipt.saved')),
              onError: (err) => toast.show(errorMessage(err), 'danger'),
            },
          );
        }}
      />
      {open?.kind === 'edit' && (
        <EntryFormDialog
          open
          kind={open.entry.kind}
          entry={open.entry}
          onClose={() => setOpen(null)}
        />
      )}
      <ConfirmDialog
        open={open?.kind === 'delete'}
        onClose={() => {
          remove.reset();
          setOpen(null);
        }}
        title={t('finance.detail.deleteTitle')}
        description={t('finance.detail.deleteText')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : undefined}
        onConfirm={() =>
          open &&
          remove.mutate(open.entry.id, {
            onSuccess: () => {
              toast.show(t('finance.detail.deleted'));
              setOpen(null);
            },
          })
        }
      />
    </>
  );

  return {
    rowActions: rowMenu.table<Entry>(
      (e) => `${e.description || formatEuro(e.amount_cents)}, ${formatDate(e.booked_on)}`,
      items,
    ),
    dialogs,
  };
}
